import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import {
  type DvcSearchType,
  DvcSearchTypeSchema,
  isDvcSearchTypeAvailable,
  isSameDvcSearch,
} from "@recobra/schemas/task";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@recobra/ui/field";
import { Input } from "@recobra/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@recobra/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@recobra/ui/tooltip";

import { caseSummaryQueryOptions } from "../../cases";
import { sortByLabel } from "../../lib";
import { tasksApi } from "../../lib/api";
import type { TaskCreationFormApi } from "./useTaskCreationForm";

/** The DVC search this task asks for: the kind of lookup, and the value to run it on. */
export const DvcSearchSection: FC<{
  form: TaskCreationFormApi;
  contextType: "case" | "client";
  contextId: string;
  editingTaskId?: string;
}> = ({ form, contextType, contextId, editingTaskId }) => {
  const queryClient = useQueryClient();
  const isCaseTask = contextType === "case" && !!contextId;
  const { data: kase } = useQuery({
    ...caseSummaryQueryOptions(queryClient, contextId),
    enabled: isCaseTask,
  });
  const { data: dvcTasks } = useQuery({
    queryKey: ["tasks", "dvc-searches", contextId],
    queryFn: () =>
      tasksApi.listAllTasks({ contextType: "case", contextId, system: ["search_dvc"] }),
    enabled: isCaseTask,
  });

  const isRewritingExistingSearch = !!editingTaskId;
  const incidentDate = isRewritingExistingSearch ? undefined : kase?.incident?.date;
  const alreadyRequested = (type: DvcSearchType, dataToSearch: string): boolean =>
    (dvcTasks ?? []).some(
      (task) =>
        task.id !== editingTaskId &&
        !!task.dvcSearchDetails &&
        isSameDvcSearch(task.dvcSearchDetails, { type, dataToSearch }),
    );

  return (
    <FieldGroup>
      <form.AppField name="dvcSearchType">
        {(field) => (
          <Field data-invalid={field.state.meta.errors[0] ? true : undefined}>
            <FieldLabel htmlFor="dvc-search-type">{m.task_dvc_search_type_label()}</FieldLabel>
            <Select
              value={field.state.value || null}
              onValueChange={(value) => field.handleChange((value ?? "") as DvcSearchType | "")}
              itemToStringLabel={(dvcSearchType: DvcSearchType) =>
                m.dvc_search_type_label({ dvcSearchType })
              }
            >
              <SelectTrigger id="dvc-search-type" className="w-full">
                <SelectValue placeholder={m.task_dvc_search_type_placeholder()} />
              </SelectTrigger>
              <SelectContent>
                {sortByLabel(DvcSearchTypeSchema.options, (dvcSearchType) =>
                  m.dvc_search_type_label({ dvcSearchType }),
                ).map((dvcSearchType) => (
                  <DvcSearchTypeOption
                    key={dvcSearchType}
                    dvcSearchType={dvcSearchType}
                    unavailable={!isDvcSearchTypeAvailable(dvcSearchType, incidentDate)}
                  />
                ))}
              </SelectContent>
            </Select>
            {field.state.meta.errors[0] ? (
              <FieldError>{field.state.meta.errors[0]}</FieldError>
            ) : null}
          </Field>
        )}
      </form.AppField>

      <form.AppField name="dvcSearchData">
        {(field) => (
          <Field data-invalid={field.state.meta.errors[0] ? true : undefined}>
            <FieldLabel htmlFor="dvc-search-data">{m.task_dvc_search_data_label()}</FieldLabel>
            <Input
              id="dvc-search-data"
              value={field.state.value}
              onChange={(event) => field.handleChange(event.target.value)}
            />
            {field.state.meta.errors[0] ? (
              <FieldError>{field.state.meta.errors[0]}</FieldError>
            ) : null}
          </Field>
        )}
      </form.AppField>

      <form.Subscribe
        selector={(state) => ({
          type: state.values.dvcSearchType,
          dataToSearch: state.values.dvcSearchData.trim(),
        })}
      >
        {({ type, dataToSearch }) =>
          type && dataToSearch && alreadyRequested(type, dataToSearch) ? (
            <FieldDescription className="flex items-center gap-1 text-warning">
              <TriangleAlert className="size-3.5 shrink-0" aria-hidden="true" />
              <span>{m.task_dvc_search_duplicate()}</span>
            </FieldDescription>
          ) : null
        }
      </form.Subscribe>
    </FieldGroup>
  );
};

const DvcSearchTypeOption: FC<{ dvcSearchType: DvcSearchType; unavailable: boolean }> = ({
  dvcSearchType,
  unavailable,
}) => {
  const item = (
    <SelectItem
      value={dvcSearchType}
      disabled={unavailable}
      className={unavailable ? "pointer-events-auto!" : undefined}
    >
      {m.dvc_search_type_label({ dvcSearchType })}
    </SelectItem>
  );

  if (!unavailable) return item;

  return (
    <Tooltip>
      <TooltipTrigger render={item} />
      <TooltipContent side="right">{m.task_dvc_search_type_unavailable()}</TooltipContent>
    </Tooltip>
  );
};
